import {
  TYPE_ASSET,
  TYPE_GIFT,
  TYPE_RETIREMENT,
  AUTOMOBILE,
  BONDS,
  BRIDGE_LOAN,
  CASH_ON_HAND,
  CERTIFICATE_OF_DEPOSIT,
  CHECKING,
  DEPOSIT_ON_SALES_CONTRACT,
  EQUITY_FROM_SUBJECT_PROPERTY,
  EQUITY_ON_SOLD_PROPERTY,
  GIFT_EMPLOYER,
  GIFT_GOVERNMENT_ASSISTANCE,
  GIFT_NON_SELLER_FUNDED_NON_PROFIT,
  GIFT_RELATIVE,
  INDIVIDUAL_DEVELOPMENT_ACCOUNT,
  LIFE_INSURANCE_CASH_VALUE,
  LOT_EQUITY,
  MONEY_MARKET_FUND,
  MUTUAL_FUND,
  OTHER_TYPE_OF_DOWN_PAYMENT,
  RENT_WITH_OPTION_TO_PURCHASE,
  RETIREMENT_FUNDS,
  SALE_OF_CHATTEL,
  SAVING,
  SECURED_BORROWED_FUNDS,
  STOCK_OPTIONS,
  STOCKS,
  SWEAT_EQUITY,
  TRADE_EQUITY,
  TRUST_FUNDS,
  UNSECURED_BORROWED_FUNDS
} from "../../constants/reserves_account_types";

/**
 * MISMO AssetType -> LANIS reserve type / sub-type.
 *
 * This is the inverse of ASSET_TYPE_MISMO in exporters/asset.js. It MUST stay
 * in step with it: the exporter writes 38 different asset types, and this map
 * previously understood only three (CheckingAccount, SavingsAccount,
 * RetirementFund). Every other type fell through and left sub_type as "",
 * which is not a key in PERCENT_CREDITED_AGENCIES — so the reserve
 * calculation did new Decimal(funds).times(undefined) and threw, taking down
 * the whole Reserves and Down Payment pages. A real file with an
 * "Edward Jones / Stock" account was enough to do it.
 *
 * Where the export collapses several LANIS sub-types onto one MISMO type
 * (Stock <- both STOCKS and STOCKS_BONDS), the inverse picks the canonical one.
 */
const ASSET_TYPE_MAP = {
  Automobile: { type: TYPE_ASSET, sub_type: AUTOMOBILE },
  Bond: { type: TYPE_ASSET, sub_type: BONDS },
  SavingsBond: { type: TYPE_ASSET, sub_type: BONDS },
  BridgeLoanNotDeposited: { type: TYPE_ASSET, sub_type: BRIDGE_LOAN },
  CashOnHand: { type: TYPE_ASSET, sub_type: CASH_ON_HAND },
  CertificateOfDepositTimeDeposit: {
    type: TYPE_ASSET,
    sub_type: CERTIFICATE_OF_DEPOSIT
  },
  CheckingAccount: { type: TYPE_ASSET, sub_type: CHECKING },
  EarnestMoneyCashDepositPaidBySeller: {
    type: TYPE_ASSET,
    sub_type: DEPOSIT_ON_SALES_CONTRACT
  },
  EarnestMoneyCashDepositTowardPurchase: {
    type: TYPE_ASSET,
    sub_type: DEPOSIT_ON_SALES_CONTRACT
  },
  EquityFromSubjectProperty: {
    type: TYPE_ASSET,
    sub_type: EQUITY_FROM_SUBJECT_PROPERTY
  },
  ProceedsFromRealEstateSold: {
    type: TYPE_ASSET,
    sub_type: EQUITY_ON_SOLD_PROPERTY
  },
  ProceedsFromSaleOfNonRealEstateAsset: {
    type: TYPE_ASSET,
    sub_type: SALE_OF_CHATTEL
  },
  IndividualDevelopmentAccount: {
    type: TYPE_ASSET,
    sub_type: INDIVIDUAL_DEVELOPMENT_ACCOUNT
  },
  CashValueOfLifeInsurance: {
    type: TYPE_ASSET,
    sub_type: LIFE_INSURANCE_CASH_VALUE
  },
  LotEquity: { type: TYPE_ASSET, sub_type: LOT_EQUITY },
  MoneyMarketFund: { type: TYPE_ASSET, sub_type: MONEY_MARKET_FUND },
  MutualFund: { type: TYPE_ASSET, sub_type: MUTUAL_FUND },
  RentWithOptionToPurchase: {
    type: TYPE_ASSET,
    sub_type: RENT_WITH_OPTION_TO_PURCHASE
  },
  SavingsAccount: { type: TYPE_ASSET, sub_type: SAVING },
  SecuredBorrowedFunds: { type: TYPE_ASSET, sub_type: SECURED_BORROWED_FUNDS },
  UnsecuredBorrowedFunds: {
    type: TYPE_ASSET,
    sub_type: UNSECURED_BORROWED_FUNDS
  },
  Stock: { type: TYPE_ASSET, sub_type: STOCKS },
  StockOptions: { type: TYPE_ASSET, sub_type: STOCK_OPTIONS },
  SweatEquity: { type: TYPE_ASSET, sub_type: SWEAT_EQUITY },
  TradeEquity: { type: TYPE_ASSET, sub_type: TRADE_EQUITY },
  TrustFunds: { type: TYPE_ASSET, sub_type: TRUST_FUNDS },
  TrustAccount: { type: TYPE_ASSET, sub_type: TRUST_FUNDS },
  Other: { type: TYPE_ASSET, sub_type: OTHER_TYPE_OF_DOWN_PAYMENT },

  // Retirement
  RetirementFund: { type: TYPE_RETIREMENT, sub_type: RETIREMENT_FUNDS },

  // Gifts
  GiftFundsEmployer: { type: TYPE_GIFT, sub_type: GIFT_EMPLOYER },
  GiftFundsGovernmentalAgency: {
    type: TYPE_GIFT,
    sub_type: GIFT_GOVERNMENT_ASSISTANCE
  },
  GiftFundsNonProfitOrganization: {
    type: TYPE_GIFT,
    sub_type: GIFT_NON_SELLER_FUNDED_NON_PROFIT
  },
  GiftFundsFamilyMember: { type: TYPE_GIFT, sub_type: GIFT_RELATIVE }
};

export function importAssetDetail(obj, { reserve }) {
  if (!reserve) return;

  if (obj.AssetAccountIdentifier) {
    reserve.account_number = obj.AssetAccountIdentifier;
  }
  if (obj.AssetCashOrMarketValueAmount != null) {
    reserve.funds = String(obj.AssetCashOrMarketValueAmount);
  }

  if (obj.AssetType) {
    const mapped = ASSET_TYPE_MAP[obj.AssetType];
    if (mapped) {
      reserve.type = mapped.type;
      reserve.sub_type = mapped.sub_type;
    } else {
      // Never leave sub_type blank — downstream reserve math looks the
      // sub-type up in a percent-credited table and a missing key used to
      // crash the page. "Other" is a real, selectable choice the LO can
      // correct.
      console.warn("Unmapped MISMO AssetType:", obj.AssetType);
      reserve.type = TYPE_ASSET;
      reserve.sub_type = OTHER_TYPE_OF_DOWN_PAYMENT;
    }
  }
}

export function importAssetHolder(obj, { reserve }) {
  if (reserve && obj.NAME && obj.NAME.FullName) {
    reserve.bank_name = obj.NAME.FullName;
  }
}
